<template>
  <VContainer id="technician-dashboard" fluid class="mx-0 pa-0">
    <VRow dense>
      <v-toolbar color="primary" density="comfortable">
        <v-toolbar-title class="text-white">
          {{ sessionStore.loggedUser.username }}
        </v-toolbar-title>
        <v-spacer />
        <div class="d-flex ga-2">
          <v-tooltip location="left">
            <template #activator="{ props }">
              <v-btn icon variant="text" v-bind="props" @click="save">
                <v-icon>mdi-content-save-all</v-icon>
              </v-btn>
            </template>
            Sauvegarder
          </v-tooltip>
        </div>
      </v-toolbar>
    </VRow>
    <VRow dense>
      <VCol cols="12" md="6">
        <v-card class="card">
          <v-card-title class="d-flex align-center justify-space-between card-header"
            style="border-left: 4px solid #facd55">
            <span>Tournée du jour</span>
          </v-card-title>
          <v-divider />
          <v-card-text>
            <v-text-field v-model="taskList.codeTournee" label="Code tournée" density="compact" readonly />
            <v-autocomplete v-model="taskList.taskListStatus" :items="taskListStatuses" density="compact"
              label="Status" />
          </v-card-text>
        </v-card>
      </VCol>
      <VCol cols="12" md="6">
        <v-card class="card">
          <v-card-title class="d-flex align-center justify-space-between card-header"
            style="border-left: 4px solid #ffbbff">
            <span>Véhicule</span>
          </v-card-title>
          <v-divider />
          <v-card-text>
            <v-autocomplete v-model="vehicle.id" :items="vehicles" item-title="label" item-value="id" density="compact"
              label="Véhicule" />
          </v-card-text>
          <v-divider />
          <!-- inventory section title -->
          <template v-if="vehicleInventoryItems.length">
            <v-card-subtitle class="inventory-title">Contenu du véhicule</v-card-subtitle>
            <v-card-text class="inventory-scroll">
              <v-list dense>
                <v-list-item v-for="(item, idx) in vehicleInventoryItems" :key="item.id"
                  :class="idx % 2 === 0 ? 'even-inv' : 'odd-inv'">
                  <v-list-item-title class="d-flex align-center justify-space-between">
                    <span>{{ item.sparepartName }}</span>
                    <v-chip small color="primary" variant="tonal" class="ml-2">
                      {{ item.quantity }}
                    </v-chip>
                  </v-list-item-title>
                  <v-list-item-subtitle v-if="item.sparepartFrenchName">
                    {{ item.sparepartFrenchName }}
                  </v-list-item-subtitle>
                </v-list-item>
              </v-list>
            </v-card-text>
            <v-divider />
          </template>
        </v-card>
      </VCol>
    </VRow>
    <VRow dense>
      <VCol cols="12" md="6">
        <v-card class="card">
          <v-card-title class="d-flex align-center justify-space-between card-header"
            style="border-left: 4px solid #aacc80">
            <span>Addresse de départ</span>
          </v-card-title>
          <v-divider />
          <v-card-text>
            <v-text-field label="Numéro" v-model="taskList.startAddress.numero" density="compact" />
            <v-text-field label="Rue" v-model="taskList.startAddress.rue" density="compact" />
            <v-text-field label="Code postal" v-model="taskList.startAddress.codepostale" density="compact" />
            <v-text-field label="Ville" v-model="taskList.startAddress.ville" density="compact" />
          </v-card-text>
        </v-card>
      </VCol>
      <VCol cols="12" md="6">
        <v-card class="card">
          <v-card-title class="d-flex align-center justify-space-between card-header"
            style="border-left: 4px solid #ff0000">
            <span>Addresse d'arrivée</span>
          </v-card-title>
          <v-divider />
          <v-card-text>
            <v-text-field label="Numéro" v-model="taskList.endAddress.number" density="compact" />
            <v-text-field label="Rue" v-model="taskList.endAddress.street" density="compact" />
            <v-text-field label="Code postal" v-model="taskList.endAddress.postcode" density="compact" />
            <v-text-field label="Ville" v-model="taskList.endAddress.city" density="compact" />
          </v-card-text>
        </v-card>
      </VCol>
    </VRow>
    <VRow>
      <VCol cols="12" md="6">
        <v-card class="card">
          <v-toolbar style="border-left: 4px solid #0000ff" density="compact" class="card-header laundries-header">
            <v-toolbar-title>
              Laveries à visiter
              <v-chip size="small" color="primary" variant="tonal">
                {{ tasks.length }}
              </v-chip>
            </v-toolbar-title>

            <v-spacer />

            <v-btn icon size="small" @click="optimizeRoute">
              <v-icon>mdi-map-marker-distance</v-icon>
            </v-btn>
          </v-toolbar>

          <v-divider />

          <v-card-text class="laundry-scroll">
            <draggable v-model="tasks" item-key="id" handle=".drag-handle" animation="200" ghost-class="drag-ghost"
              @end="onDragEnd">
              <template #item="{ element: task, index }">
                <v-list-item class="laundry-row" link :to="`/on-going-interventions/${task.id}`" :class="[
                  index % 2 === 0 ? 'even-row' : 'odd-row',
                  task.priority === 'URGENT' && 'urgent-row',
                ]">
                  <template #prepend>
                    <!-- Drag Handle -->
                    <v-icon class="drag-handle mr-2" size="18"> mdi-drag </v-icon>

                    <!-- Rank -->
                    <v-chip size="x-small" class="mr-2">
                      {{ task.rank }}
                    </v-chip>
                  </template>

                  <v-list-item-title class="d-flex align-center">
                    <span :class="{ 'text-decoration-line-through': task.taskStatus === 'FAITE' }">{{ task.name }}</span>

                    <v-chip v-if="task.priority === 'urgent'" size="x-small" color="error" variant="tonal"
                      class="ml-2">
                      Urgent
                    </v-chip>
                  </v-list-item-title>

                  <v-list-item-subtitle>
                    <p>{{ task.number }} {{ task.street }}</p>
                    <p>{{ task.postcode }} {{ task.city }}</p>
                  </v-list-item-subtitle>

                  <template #append>
                    <v-btn icon size="x-small" @click.stop="moveUp(index)">
                      <v-icon size="16">mdi-chevron-up</v-icon>
                    </v-btn>

                    <v-btn icon size="x-small" @click.stop="moveDown(index)">
                      <v-icon size="16">mdi-chevron-down</v-icon>
                    </v-btn>
                  </template>
                </v-list-item>
              </template>
            </draggable>
          </v-card-text>
        </v-card>
      </VCol>

      <VCol cols="12" md="6">
        <v-card class="card directions-card">
          <v-card-title class="d-flex align-center justify-space-between card-header"
            style="border-left: 4px solid #1565c0">
            <span>Directions</span>
          </v-card-title>
          <v-divider />
          <v-card-text class="pa-0 flex-grow-1">
            <RouteMapComponent
              v-if="taskList.id"
              :start-address="debouncedStartAddress"
              :end-address="debouncedEndAddress"
              :tasks="tasks"
            />
            <div v-else class="map-placeholder">
              <v-icon size="48" color="grey-lighten-1">mdi-map-outline</v-icon>
              <p class="text-medium-emphasis mt-2">Aucune tournée chargée</p>
            </div>
          </v-card-text>
        </v-card>
      </VCol>
    </VRow>
  </VContainer>
</template>

<script setup lang="ts">
import RouteMapComponent from "@/components/RouteMapComponent.vue";
import { useAppStore, useSessionStore } from "@/store";
import { Address, EndAddress, Task, TaskList, TaskListStatus, Vehicle, VehicleInventoryItem } from "@/types";
import { getTasksByTaskListId, updateTaskRank } from "@/utils/task-api";
import { getFilteredTaskLists, postOptimizeRoute, putTaskList } from "@/utils/tasklist-api";
import { getAllVehicles, getVehicleByTaskListId, getVehicleInventoryItems } from "@/utils/vehicle-api";
import { assignVehicleToTaskList } from "@/utils/tasklist-api";
import { onBeforeUnmount, onMounted, ref, watch } from "vue";
import draggable from "vuedraggable";
1;
const appStore = useAppStore();
const sessionStore = useSessionStore();

const createEmptyTaskList = (): TaskList => ({
  name: "",
  codeTournee: "",
  dateDebut: new Date(),
  dateFin: new Date(),
  technicienIds: [],
  technicienNames: [],
  taskListStatus: TaskListStatus.A_FAIRE,
  label: "",
  startAddress: {
    numero: "",
    rue: "",
    codepostale: "",
    ville: "",
  },
  endAddress: {
    street: "",
    city: "",
    number: "",
    postcode: "",
  },
});

const createEmptyVehicle = (): Vehicle => ({
  id: "",
  label: "",
  plateNumber: "",
});

const taskListStatuses = [TaskListStatus.EN_COURS, TaskListStatus.FAITE];
const taskList = ref<TaskList>(createEmptyTaskList());
const tasks = ref<Task[]>([]);
const vehicles = ref<Vehicle[]>([]);
const vehicle = ref<Vehicle>(createEmptyVehicle());
const vehicleInventoryItems = ref<VehicleInventoryItem[]>([]);
const loading = ref(false);
const debouncedStartAddress = ref<Address>({ ...taskList.value.startAddress });
const debouncedEndAddress = ref<EndAddress>({ ...taskList.value.endAddress });

let mapAddressDebounceTimeout: ReturnType<typeof setTimeout> | null = null;

function queueDirectionsAddressUpdate() {
  if (mapAddressDebounceTimeout) {
    clearTimeout(mapAddressDebounceTimeout);
  }

  mapAddressDebounceTimeout = setTimeout(() => {
    debouncedStartAddress.value = { ...taskList.value.startAddress };
    debouncedEndAddress.value = { ...taskList.value.endAddress };
  }, 2000);
}

watch(
  () => taskList.value.startAddress,
  () => queueDirectionsAddressUpdate(),
  { deep: true, immediate: true }
);

watch(
  () => taskList.value.endAddress,
  () => queueDirectionsAddressUpdate(),
  { deep: true, immediate: true }
);

onBeforeUnmount(() => {
  if (mapAddressDebounceTimeout) {
    clearTimeout(mapAddressDebounceTimeout);
    mapAddressDebounceTimeout = null;
  }
});

async function save() {
  try {
    loading.value = true;
    await putTaskList(taskList.value);
    appStore.sendSuccessNotice("TaskList has been updated.");
  } catch (e) {
    // Ignore because it's handled in axios service
  } finally {
    loading.value = false;
  }
}

async function optimizeRoute() {
  if (!taskList.value.startAddress) {
    appStore.sendErrorNotice("L'adresse de départ est requise pour optimiser la route");
    return;
  }
  if (!taskList.value.endAddress) {
    appStore.sendErrorNotice("L'adresse d'arrivée est requise pour optimiser la route");
    return;
  }

  try {
    loading.value = true;
    await postOptimizeRoute(taskList.value.id);
    tasks.value = await getTasksByTaskListId(taskList.value.id);
  } catch (e) {
    // Ignore because it's handled in axios service
  } finally {
    loading.value = false;
  }
}

async function onDragEnd() {
  // Recalculate ranks
  tasks.value.forEach((l, i) => {
    l.rank = i + 1;
  });

  await saveRanks();
}

async function saveRanks() {
  try {
    await Promise.all(tasks.value.map((task) => updateTaskRank(task)));
  } catch (e) {
    // Ignore because it's handled in axios service
  }
}


function moveUp(index: number) {
  if (index === 0) return;
  tasks.value.splice(index - 1, 0, tasks.value.splice(index, 1)[0]);
  onDragEnd();
}

function moveDown(index: number) {
  if (index === tasks.value.length - 1) return;
  tasks.value.splice(index + 1, 0, tasks.value.splice(index, 1)[0]);
  onDragEnd();
}

onMounted(async () => {
  try {
    const userId = sessionStore.loggedUser.id;
    if (!userId) {
      return;
    }

    const taskLists = await getFilteredTaskLists(
      Array.of(TaskListStatus.ASSIGNE, TaskListStatus.EN_COURS, TaskListStatus.FAITE),
      userId,
      new Date()
    );
    if (taskLists.length === 0) {
      appStore.sendInfoNotice("Aucune tournée assignée pour aujourd'hui");
      return;
    }

    if (taskLists.length > 1) {
      appStore.sendErrorNotice("Plusieurs tournées assignées pour aujourd'hui");
      return;
    }

    taskList.value = taskLists[0];
    const [allTasks, allVehicles] = await Promise.all([
      getTasksByTaskListId(taskList.value.id),
      getAllVehicles(),
    ]);
    tasks.value = allTasks;
    vehicles.value = allVehicles;

    vehicle.value = await getVehicleByTaskListId(taskList.value.id);
    if (vehicle.value.id) {
      vehicleInventoryItems.value = await getVehicleInventoryItems(vehicle.value.id);
    }

    // watch for vehicle selection changes and reload inventory
    watch(
      () => vehicle.value.id,
      async (id) => {
        if (id) {
          vehicleInventoryItems.value = await getVehicleInventoryItems(id);
          if (taskList.value.id) {
            try {
              await assignVehicleToTaskList(taskList.value.id, id);
            } catch (e) {
              // Ignore because it's handled in axios service
            }
          }
        } else {
          vehicleInventoryItems.value = [];
        }
      }
    );
  } catch (error) {
    // Ignore
  }
});
</script>

<style scoped>
.card {
  height: 320px;
  display: flex;
  flex-direction: column;
}

.card-header {
  padding-left: 12px;
}

.laundry-scroll {
  max-height: 320px;
  overflow-y: auto;
}

.laundry-row {
  padding: 10px 8px;
}

.drag-handle {
  cursor: grab;
  opacity: 0.6;
}

.drag-ghost {
  opacity: 0.4;
}

.even-row {
  background: rgba(0, 0, 0, 0.02);
}

.odd-row {
  background: rgba(0, 0, 0, 0.08);
}

.urgent-row {
  border-left: 4px solid rgb(var(--v-theme-error));
}

.inventory-scroll {
  max-height: 160px;
  overflow-y: auto;
}

.inventory-title {
  font-weight: bold;
  padding-left: 16px;
  padding-top: 8px;
}

.even-inv {
  background-color: #f0f8ff;
}

.odd-inv {
  background-color: #e0f0ff;
}

.laundries-header {
  background-color: #e3f2fd !important;
}

.directions-card {
  height: 320px;
  display: flex;
  flex-direction: column;
}

.directions-card .v-card-text {
  overflow: hidden;
  height: 100%;
}

.map-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 240px;
}
</style>
